import { type FunctionComponent, type ReactNode, useEffect, useState } from 'react';

import { DeviceInfo } from '../../deviceInfo/deviceInfo';
import { Logger } from '../../logger/Logger';
import { useIsLargerThanWide } from '../../styles/hooks/media';
import { appWindow } from '../../utils/appWindow/AppWindow';
import { useLegacyContext } from '../legacyMode/LegacyContextProvider';

import styles from './Fullscreen.module.css';

const iOS_MinimalHeight = 25;

export const IOSFullscreen: FunctionComponent<{ isReadyToRender: boolean }> = ({ isReadyToRender }): ReactNode => {
  const { isLegacyMode } = useLegacyContext();
  const isWide = useIsLargerThanWide();

  const windowObject = appWindow.appRootWindow;

  const isAppleMobile = DeviceInfo.getPlatform().IS_IOS;
  const [showFullscreen, setShowFullscreen] = useState<boolean>(false);

  useEffect(() => {
    if (!isWide && isAppleMobile) {
      const media = windowObject.matchMedia('(orientation: landscape)');

      const toggleFullScreenClass = (): void => {
        setTimeout(() => {
          const isFullScreenEnable = windowObject.screen.width - windowObject.innerHeight <= iOS_MinimalHeight;
          if (!isFullScreenEnable) {
            windowObject.document.documentElement.scrollTo(0, 0);
          }
          windowObject.document.documentElement.classList.toggle('ios-fullscreen', isFullScreenEnable);
        }, 350);
      };

      const mediaFunc = function ({ matches }: { matches: boolean }, isInitial: boolean = false): void {
        Logger.logEvent('rotate_' + isInitial, {
          additional: matches ? 'landscape' : 'portrait',
        });

        if (matches) {
          setShowFullscreen(true);
          windowObject.document
            .querySelector<HTMLElement>('.' + styles.FullScreen)
            ?.style.setProperty('height', 'calc(100vh + 300px)');
          toggleFullScreenClass();
          windowObject.addEventListener('resize', toggleFullScreenClass);
        } else {
          setShowFullscreen(false);
          windowObject.document.querySelector<HTMLElement>('.' + styles.FullScreen)?.style.removeProperty('height');
          toggleFullScreenClass();
          windowObject.scrollTo(0, 1);
          windowObject.removeEventListener('resize', toggleFullScreenClass);
        }
      };

      mediaFunc(media, true);
      media.addEventListener('change', mediaFunc);
      return (): void => {
        media.removeEventListener('change', mediaFunc);
        windowObject.removeEventListener('resize', toggleFullScreenClass);
      };
    }
  }, [isAppleMobile, isLegacyMode, isWide, windowObject]);

  // This is fixing super weird iOS 26 issue, that happens after device rotation UIC-2579
  useEffect(() => {
    if (!isAppleMobile) return;
    let timeOut: number | undefined;
    const listener = (): void => {
      timeOut = window.setTimeout(() => {
        const root = document.getElementById('root');
        root?.style.setProperty('width', window.innerWidth + 'px');
        root?.style.setProperty('height', window.innerHeight + 'px');
      }, 500);
    };

    screen.orientation?.addEventListener('change', listener);
    window.addEventListener('orientationchange', listener);
    return (): void => {
      window.clearTimeout(timeOut);
      window.removeEventListener('orientationchange', listener);
      screen.orientation?.removeEventListener('change', listener);
    };
  }, [isAppleMobile]);

  if (isWide || !isAppleMobile) return null;
  if (isAppleMobile && windowObject !== windowObject.parent) return null;

  return (
    <div className={styles.FullScreen} style={{ zIndex: showFullscreen ? undefined : -999 }}>
      {isReadyToRender ? <div className={styles.inner} /> : null}
    </div>
  );
};
